/**
 * Pagination controls.
 *
 * Design: https://www.figma.com/file/01KWBdMZg5QM9SRS1pQq0z/Design-System----Robot-Styles?type=design&node-id=96%3A1175&t=6Iw3ZBGxaKu7LiV0-1
 */
import {MOON_500} from '@wandb/weave/common/css/globals.styles';
// @todo: Migrate to @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components/Button';
import React from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import styled from 'styled-components';

export const PaginationWrapper = styled.div`
  display: flex;
  align-items: center;
`;
PaginationWrapper.displayName = 'S.PaginationWrapper';

export const PaginationText = styled.span`
  color: ${MOON_500};
  margin-right: 16px;
`;
PaginationText.displayName = 'S.PaginationText';

type PaginationProps = {
  itemCount: number;
  page: number; // 0-indexed
  perPage: number;
  onPageChange: (page: number) => void;
  allItemsLoaded?: boolean;
};

/**
 * Helper function to slice arrays based on page indices
 * Note: negative page yields an empty slice
 */
export function sliceArray(arr: any[], page: number, perPage: number) {
  // look into negative indices
  return arr.slice(page * perPage, perPage + page * perPage) ?? [];
}

/**
 * Function that returns 0-indexed page values
 * Don't need to worry about negative page/lastPage as sliceArray()
 * takes care of it
 */
export function paginate(
  itemCount: number,
  perPage: number,
  page: number
): {start: number; end: number; lastPage: number} {
  const start = Math.min(page * perPage + 1, itemCount);
  const end = Math.min(itemCount, start + perPage - 1);

  const lastPage = Math.max(Math.ceil(itemCount / perPage) - 1, 0); // 0-indexed
  return {start, end, lastPage};
}

export const Pagination = ({
  itemCount,
  page,
  perPage,
  onPageChange,
  allItemsLoaded = true,
}: PaginationProps) => {
  const {start, end, lastPage} = paginate(itemCount, perPage, page);
  const hasPrevPage = page !== 0;
  const hasNextPage = page !== lastPage || !allItemsLoaded;

  return (
    <PaginationWrapper>
      <PaginationText>
        {start.toLocaleString()}-{end.toLocaleString()} of{' '}
        {allItemsLoaded ? itemCount.toLocaleString() : 'many'}
      </PaginationText>
      <Button
        icon="chevron-back"
        size="small"
        variant="ghost"
        disabled={!hasPrevPage}
        onClick={() => onPageChange(page - 1)}
      />
      <Button
        icon="chevron-next"
        size="small"
        variant="ghost"
        disabled={!hasNextPage}
        onClick={() => onPageChange(page + 1)}
      />
    </PaginationWrapper>
  );
};
